import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { tokens } from '../../theme/tokens';
import {
  Alert,  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Snackbar,
  Stack,
  TextField,  Tooltip,
  Typography
} from '@mui/material';
import UnitPageShell from '../../components/shared/UnitPageShell';
import AddIcon from '@mui/icons-material/Add';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useAuth } from '../../context/AuthContext';
import { apiConfig } from '../../utils/apiConfig';

type SnackState = { message: string; severity: 'success' | 'error' } | null;

type FaqEntry = {
  id: number;
  alias: string;
  question: string;
  questionTimestamp: string;
  imageUrl: string | null;
  answerText: string | null;
  answerAdminAlias: string | null;
  answerTimestamp: string | null;
  status: string;
};

// paragraphSx removed - use inline style with darkMode in component
const answerBoxSx = {
  mt: 2,
  px: 2.5,
  py: 2,
  borderRadius: 3,
  backgroundColor: 'rgba(13,53,89,0.06)',
  borderLeft: '4px solid #0D3559'
} as const;

const FAQ_IMAGE_BASE = 'https://app.portal.se-cloud.de/Bilder/faq/';
const ensureFaqImageUrl = (path: string | null | undefined): string | null => {
  if (!path) return null;
  const trimmed = path.trim();
  if (!trimmed) return null;
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  if (trimmed.startsWith('Bilder/faq/')) return `https://app.portal.se-cloud.de/${trimmed}`;
  if (trimmed.startsWith('Bilder/')) return `https://app.portal.se-cloud.de/${trimmed}`;
  if (trimmed.startsWith('faq/')) return `${FAQ_IMAGE_BASE}${trimmed.substring(4)}`;
  return `${FAQ_IMAGE_BASE}${trimmed}`;
};

const formatTimestamp = (value: unknown): string | null => {
  if (!value || typeof value !== 'string') return null;
  const trimmed = value.trim();
  if (!trimmed || trimmed.toLowerCase() === 'null') return null;
  const normalized = trimmed.replace(' ', 'T');
  const date = new Date(/z$/i.test(normalized) ? normalized : `${normalized}Z`);
  if (Number.isNaN(date.getTime())) {
    return trimmed;
  }
  const pad = (n: number) => n.toString().padStart(2, '0');
  const dd = pad(date.getDate());
  const mm = pad(date.getMonth() + 1);
  const yyyy = date.getFullYear();
  const hh = pad(date.getHours());
  const mi = pad(date.getMinutes());
  return `${dd}.${mm}.${yyyy} ${hh}:${mi}`;
};

const mapFaqEntry = (raw: any): FaqEntry => {
  const alias = typeof raw?.question_user_alias === 'string' && raw.question_user_alias.trim()
    ? raw.question_user_alias.trim()
    : 'Unbekannt';
  const answerText = typeof raw?.answer_text === 'string' && raw.answer_text.trim()
    ? raw.answer_text.trim()
    : null;
  const answerAdminAlias = typeof raw?.answer_admin_alias === 'string' && raw.answer_admin_alias.trim()
    ? raw.answer_admin_alias.trim()
    : null;
  const status = typeof raw?.status === 'string' ? raw.status : '';

  return {
    id: Number(raw?.id) || 0,
    alias,
    question: typeof raw?.question_text === 'string' ? raw.question_text : '',
    questionTimestamp: formatTimestamp(raw?.question_timestamp) ?? 'N/A',
    imageUrl: ensureFaqImageUrl(raw?.image_path),
    answerText,
    answerAdminAlias,
    answerTimestamp: formatTimestamp(raw?.answer_timestamp),
    status
  };
};

const Page: React.FC = () => {
  const { darkMode } = useTheme();
  const { token, canManageFaq, canViewFaq, canAskFaqQuestion, canAnswerFaq, canUploadFaqImages } = useAuth();

  const [entries, setEntries] = useState<FaqEntry[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [snackbar, setSnackbar] = useState<SnackState>(null);

  const [questionDialogOpen, setQuestionDialogOpen] = useState(false);
  const [questionText, setQuestionText] = useState('');
  const [questionFile, setQuestionFile] = useState<File | null>(null);
  const [questionPreview, setQuestionPreview] = useState<string | null>(null);
  const [questionSubmitting, setQuestionSubmitting] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const [answerDialogEntry, setAnswerDialogEntry] = useState<FaqEntry | null>(null);
  const [answerText, setAnswerText] = useState('');
  const [answerSubmitting, setAnswerSubmitting] = useState(false);

  useEffect(() => {
    if (!questionFile) {
      setQuestionPreview(null);
      return;
    }
    const objectUrl = URL.createObjectURL(questionFile);
    setQuestionPreview(objectUrl);
    return () => URL.revokeObjectURL(objectUrl);
  }, [questionFile]);

  const showSnackbar = useCallback((message: string, severity: 'success' | 'error') => {
    setSnackbar({ message, severity });
  }, []);

  const resetQuestionDialog = useCallback(() => {
    setQuestionDialogOpen(false);
    setQuestionText('');
    setQuestionFile(null);
    setQuestionPreview(null);
    setQuestionSubmitting(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, []);

  const closeAnswerDialog = useCallback(() => {
    setAnswerDialogEntry(null);
    setAnswerText('');
    setAnswerSubmitting(false);
  }, []);

  const fetchFaqEntries = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const url = `${apiConfig.apiUrl}?action=getFaqEntries`;
      const response = await fetch(url, { method: 'GET', headers: token ? { Authorization: `Bearer ${token}` } : {} });
      if (!response.ok) {
        throw new Error(`Serverfehler: ${response.status}`);
      }
      const data = await response.json();
      if (!Array.isArray(data)) {
        throw new Error('Unerwartete Serverantwort.');
      }
      const mapped = data.map(mapFaqEntry).sort((a, b) => b.id - a.id);
      setEntries(mapped);
    } catch (err: any) {
      setError(err?.message || 'Fragen konnten nicht geladen werden.');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchFaqEntries();
  }, [fetchFaqEntries]);

  const handleQuestionSubmit = useCallback(async () => {
    if (!token) {
      showSnackbar('Bitte zuerst einloggen, um eine Frage zu stellen.', 'error');
      return;
    }
    if (!questionText.trim()) {
      showSnackbar('Die Frage darf nicht leer sein.', 'error');
      return;
    }

    setQuestionSubmitting(true);
    try {
      const formData = new FormData();
      formData.append('action', 'submitFaqQuestion');
      formData.append('question_text', questionText.trim());
      if (questionFile) {
        formData.append('image_file', questionFile);
      }

      const response = await fetch(apiConfig.apiUrl, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData
      });

      const data = await response.json().catch(() => null);
      if (!response.ok || !data) {
        throw new Error(data?.error || `Serverfehler: ${response.status}`);
      }
      if (data.success) {
        showSnackbar(data.message || 'Frage erfolgreich gesendet.', 'success');
        resetQuestionDialog();
        fetchFaqEntries();
      } else {
        throw new Error(data.error || 'Frage konnte nicht gesendet werden.');
      }
    } catch (err: any) {
      setQuestionSubmitting(false);
      showSnackbar(err?.message || 'Fehler beim Senden der Frage.', 'error');
    }
  }, [fetchFaqEntries, questionFile, questionText, resetQuestionDialog, showSnackbar, token]);

  const handleAnswerSubmit = useCallback(async () => {
    if (!token || !canAnswerFaq || !answerDialogEntry) {
      showSnackbar('Keine Berechtigung für diese Aktion.', 'error');
      return;
    }
    if (!answerText.trim()) {
      showSnackbar('Antwort darf nicht leer sein.', 'error');
      return;
    }
    setAnswerSubmitting(true);
    try {
      const payload = {
        action: 'answerFaqQuestion',
        faq_entry_id: answerDialogEntry.id,
        answer_text: answerText.trim()
      };
      const response = await fetch(apiConfig.apiUrl, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });
      const data = await response.json().catch(() => null);
      if (!response.ok || !data) {
        throw new Error(data?.error || `Serverfehler: ${response.status}`);
      }
      if (data.success) {
        showSnackbar(data.message || 'Antwort erfolgreich gesendet.', 'success');
        closeAnswerDialog();
        fetchFaqEntries();
      } else {
        throw new Error(data.error || 'Antwort konnte nicht gesendet werden.');
      }
    } catch (err: any) {
      setAnswerSubmitting(false);
      showSnackbar(err?.message || 'Fehler beim Senden der Antwort.', 'error');
    }
  }, [answerDialogEntry, answerText, closeAnswerDialog, fetchFaqEntries, canAnswerFaq, showSnackbar, token]);

  const handleFileSelection = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    setQuestionFile(file ?? null);
  };

  const canReply = useCallback((entry: FaqEntry) => {
    if (!canAnswerFaq) return false;
    if (entry.answerText) return false;
    return entry.status.toLowerCase() === 'offen';
  }, [canAnswerFaq]);

  const emptyStateText = useMemo(() => {
    if (loading) return 'Fragen werden geladen...';
    if (error) return error;
    return 'Noch keine Fragen vorhanden.';
  }, [error, loading]);

  const actionButtons = React.createElement(Box, { sx: { display: 'flex', gap: 1 } },
    React.createElement(Tooltip as any, { title: 'Neue Frage stellen' },
      React.createElement('span', {},
        React.createElement(IconButton, {
          color: 'inherit',
          onClick: () => {
            if (!token) {
              showSnackbar('Bitte zuerst einloggen, um eine Frage zu stellen.', 'error');
              return;
            }
            if (!canAskFaqQuestion) {
              showSnackbar('Keine Berechtigung zum Stellen von Fragen.', 'error');
              return;
            }
            setQuestionDialogOpen(true);
          },
          disabled: !token || !canAskFaqQuestion
        }, React.createElement(AddIcon))
      )
    ),
    React.createElement(Tooltip as any, { title: 'Aktualisieren' },
      React.createElement('span', {},
        React.createElement(IconButton, {
          color: 'inherit',
          onClick: fetchFaqEntries,
          disabled: loading
        }, loading ? React.createElement(CircularProgress, { size: 20, color: 'inherit' }) : React.createElement(RefreshIcon))
      )
    )
  );

  if (!canViewFaq) {
    return (
      <UnitPageShell title="FAQ" showTTS={false}>
        <Alert severity="error">Keine Berechtigung fuer die FAQ-Seite.</Alert>
      </UnitPageShell>
    );
  }

  return (
    <UnitPageShell title="FAQ" showTTS={false} actions={actionButtons}>
          {error && !loading && (
            <Alert severity="error">{error}</Alert>
          )}

          {!entries.length && !loading && (
            <Paper sx={{ p: 4, textAlign: 'center', borderRadius: 3, backgroundColor: '#ffffff' }}>
              <Typography sx={{ color: darkMode ? tokens.colors.dark.text : tokens.colors.light.text, lineHeight: 1.7 }}>{emptyStateText}</Typography>
            </Paper>
          )}

          {loading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
              <CircularProgress />
            </Box>
          )}

          {entries.map((entry) => (
            <Paper key={entry.id} sx={{ p: { xs: 3, md: 4 }, borderRadius: 3, boxShadow: '0 20px 46px rgba(13,53,89,0.12)' }}>
              <Stack spacing={2}>
                <Box>
                  <Typography variant="subtitle2" sx={{ color: darkMode ? tokens.colors.dark.text : '#0D3559', fontWeight: 600 }}>
                    Gefragt von: {entry.alias} am {entry.questionTimestamp}
                  </Typography>
                  <Typography sx={{ color: darkMode ? tokens.colors.dark.text : tokens.colors.light.text, lineHeight: 1.7 }}>{entry.question}</Typography>
                </Box>

                {entry.imageUrl && (
                  <Box
                    component="img"
                    src={entry.imageUrl}
                    alt={`Frage ${entry.id}`}
                    sx={{
                      width: '100%',
                      maxHeight: 320,
                      objectFit: 'contain',
                      borderRadius: 2,
                      border: '1px solid rgba(13,53,89,0.15)'
                    }}
                  />
                )}

                {entry.answerText ? (
                  <Box sx={answerBoxSx}>
                    <Typography variant="subtitle2" sx={{ color: darkMode ? tokens.colors.dark.text : '#0D3559', fontWeight: 600 }}>
                      Beantwortet von: {entry.answerAdminAlias ?? 'Admin'}
                      {entry.answerTimestamp ? ` am ${entry.answerTimestamp}` : ''}
                    </Typography>
                    <Typography sx={{ color: darkMode ? tokens.colors.dark.text : tokens.colors.light.text, lineHeight: 1.7 }}>{entry.answerText}</Typography>
                  </Box>
                ) : canReply(entry) ? (
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <Button
                      variant="outlined"
                      onClick={() => {
                        setAnswerDialogEntry(entry);
                        setAnswerText('');
                      }}
                    >
                      Antworten
                    </Button>
                  </Box>
                ) : null}
              </Stack>
            </Paper>
          ))}





        <Dialog open={questionDialogOpen} onClose={resetQuestionDialog} fullWidth maxWidth="sm">
          <DialogTitle>Neue Frage stellen</DialogTitle>
          <DialogContent dividers>
            <Stack spacing={2.5}>
              <TextField
                label="Ihre Frage"
                value={questionText}
                onChange={(e) => setQuestionText(e.target.value)}
                multiline
                minRows={4}
                autoFocus
                fullWidth
              />
              {canUploadFaqImages && (
              <Box>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  hidden
                  onChange={handleFileSelection}
                />
                <Button
                  variant="outlined"
                  onClick={() => fileInputRef.current?.click()}
                >
                  {questionFile ? 'Anderes Bild wählen' : 'Bild auswählen'}
                </Button>
                {questionPreview && (
                  <Box
                    component="img"
                    src={questionPreview}
                    alt="Vorschau"
                    sx={{ mt: 2, maxHeight: 240, width: '100%', objectFit: 'contain', borderRadius: 2, border: '1px solid rgba(13,53,89,0.15)' }}
                  />
                )}
              </Box>
              )}
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={resetQuestionDialog}>Abbrechen</Button>
            <Button variant="contained" onClick={handleQuestionSubmit} disabled={questionSubmitting}>
              {questionSubmitting ? 'Senden…' : 'Senden'}
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog
          open={Boolean(answerDialogEntry)}
          onClose={answerSubmitting ? undefined : closeAnswerDialog}
          fullWidth
          maxWidth="sm"
        >
          <DialogTitle>Frage beantworten</DialogTitle>
          <DialogContent dividers>
            {answerDialogEntry && (
              <Stack spacing={2.5}>
                <Typography sx={{ fontStyle: 'italic', color: darkMode ? tokens.colors.dark.text : '#0D3559' }}>
                  Frage: {answerDialogEntry.question}
                </Typography>
                <TextField
                  label="Ihre Antwort"
                  value={answerText}
                  onChange={(e) => setAnswerText(e.target.value)}
                  multiline
                  minRows={4}
                  fullWidth
                />
              </Stack>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={closeAnswerDialog} disabled={answerSubmitting}>Abbrechen</Button>
            <Button variant="contained" onClick={handleAnswerSubmit} disabled={answerSubmitting}>
              {answerSubmitting ? 'Senden…' : 'Antwort senden'}
            </Button>
          </DialogActions>
        </Dialog>

        <Snackbar
          open={Boolean(snackbar)}
          autoHideDuration={5000}
          onClose={() => setSnackbar(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          {snackbar ? (
            <Alert severity={snackbar.severity} onClose={() => setSnackbar(null)}>
              {snackbar.message}
            </Alert>
          ) : undefined}
        </Snackbar>


      </UnitPageShell>
  );
}

export default Page;
